"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Tariff tiles — pre-shipment estimate vs. post-arrival final.
 *
 * Lifecycle (per the Tariff Calculation primer):
 *   1. Pre-shipment estimate — informs landed cost during LC composition.
 *      Buyer + seller see "Tariff estimate · X · methodology Y".
 *   2. Post-arrival final — customs authority assesses + collects.
 *      Acts as a gate alongside compliance snapshot before goods release.
 *
 * UAE import: 5% duty on CIF + 5% VAT on (CIF + duty). Source: GCC Unified
 * Tariff via Mirsal2.
 * BR import: ~9% Imposto de Importação + 12% ICMS for fertilizer (state-
 * dependent — Mato Grosso shown). Illustrative; real ICMS may vary.
 */

type Jurisdiction = "UAE" | "BR";

type TariffBreakdown = {
  cifUsd: number;
  dutyPct: number;
  vatOrIcmsPct: number;
  dutyUsd: number;
  vatOrIcmsUsd: number;
  totalUsd: number;
  jurisdictionLabel: string;
  basisLabel: string;       // "CIF" or "FOB invoice"
  methodologyLabel: string; // "GCC Unified Tariff via Mirsal2" etc.
  vatOrIcmsLabel: string;   // "VAT" or "ICMS"
};

function calculate({
  cifUsd,
  jurisdiction,
  hsCode,
}: {
  cifUsd: number;
  jurisdiction: Jurisdiction;
  hsCode: string;
}): TariffBreakdown {
  if (jurisdiction === "UAE") {
    const dutyPct = 0.05;
    const vatPct  = 0.05;
    const dutyUsd = cifUsd * dutyPct;
    const vatUsd  = (cifUsd + dutyUsd) * vatPct;
    return {
      cifUsd,
      dutyPct,
      vatOrIcmsPct: vatPct,
      dutyUsd,
      vatOrIcmsUsd: vatUsd,
      totalUsd: dutyUsd + vatUsd,
      jurisdictionLabel: "UAE import · GCC Unified Tariff",
      basisLabel: "CIF",
      methodologyLabel: `HS ${hsCode} · 5% duty on CIF + 5% VAT on (CIF + duty) · Mirsal2`,
      vatOrIcmsLabel: "VAT",
    };
  }
  // BR
  const dutyPct = 0.09;
  const icmsPct = 0.12;
  const dutyUsd = cifUsd * dutyPct;
  const icmsUsd = (cifUsd + dutyUsd) * icmsPct;
  return {
    cifUsd,
    dutyPct,
    vatOrIcmsPct: icmsPct,
    dutyUsd,
    vatOrIcmsUsd: icmsUsd,
    totalUsd: dutyUsd + icmsUsd,
    jurisdictionLabel: "BR import · Imposto de Importação + ICMS",
    basisLabel: "CIF",
    methodologyLabel: `HS ${hsCode} · 9% II on CIF + 12% ICMS (Mato Grosso) · Siscomex`,
    vatOrIcmsLabel: "ICMS",
  };
}

function fmtUsd(n: number): string {
  return `USD ${Math.round(n).toLocaleString()}`;
}

/* ---------- Estimate tile (pre-shipment / LC composition) ---------- */

export function TariffEstimateTile({
  cifUsd,
  jurisdiction,
  hsCode,
  className,
}: {
  cifUsd: number;
  jurisdiction: Jurisdiction;
  hsCode: string;
  className?: string;
}) {
  const tr = useTranslations("tariffTiles");
  const t = calculate({ cifUsd, jurisdiction, hsCode });
  return (
    <div className={clsx("glass p-4", className)}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{tr("estimateKicker")}</p>
          <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t.jurisdictionLabel}</h3>
        </div>
        <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
          informational · landed cost
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
        <Cell label={`Basis (${t.basisLabel})`} value={fmtUsd(t.cifUsd)} />
        <Cell label={`Duty · ${(t.dutyPct * 100).toFixed(0)}%`}  value={fmtUsd(t.dutyUsd)} />
        <Cell label={`${t.vatOrIcmsLabel} · ${(t.vatOrIcmsPct * 100).toFixed(0)}%`} value={fmtUsd(t.vatOrIcmsUsd)} />
      </div>

      <div className="mt-3 rounded-md border border-state-financed/30 bg-state-financed/10 px-3 py-2 text-[12px]">
        <div className="flex items-center justify-between">
          <span className="text-orbitra-paper">{tr("estimatedLandedTariff")}</span>
          <span className="font-display text-lg font-semibold text-state-financed">{fmtUsd(t.totalUsd)}</span>
        </div>
        <p className="mt-0.5 text-[10px] italic text-state-financed/80">
          ~{((t.totalUsd / t.cifUsd) * 100).toFixed(1)}% of CIF · landed at customs
        </p>
      </div>

      <p className="mt-2 text-[10px] text-orbitra-muted">{t.methodologyLabel}</p>
    </div>
  );
}

/* ---------- Final tile (post-arrival / gate) ---------- */

export function TariffFinalTile({
  cifUsd,
  jurisdiction,
  hsCode,
  settled = false,
  className,
}: {
  cifUsd: number;
  jurisdiction: Jurisdiction;
  hsCode: string;
  settled?: boolean;
  className?: string;
}) {
  const tr = useTranslations("tariffTiles");
  const t = calculate({ cifUsd, jurisdiction, hsCode });
  return (
    <div
      className={clsx(
        "glass p-4",
        settled
          ? "border-policy-pass/40 shadow-[0_0_16px_rgba(45,143,78,0.10)]"
          : "border-state-financed/40",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{tr("finalKicker")}</p>
          <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t.jurisdictionLabel}</h3>
        </div>
        <span
          className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider",
            settled
              ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
              : "border-state-financed/40 bg-state-financed/10 text-state-financed",
          )}
        >
          {settled ? "settled ✓ · goods released" : "assessing"}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
        <Cell label={`Basis (${t.basisLabel})`} value={fmtUsd(t.cifUsd)} />
        <Cell label={`Duty · ${(t.dutyPct * 100).toFixed(0)}%`}  value={fmtUsd(t.dutyUsd)} />
        <Cell label={`${t.vatOrIcmsLabel} · ${(t.vatOrIcmsPct * 100).toFixed(0)}%`} value={fmtUsd(t.vatOrIcmsUsd)} />
      </div>

      <div
        className={clsx(
          "mt-3 rounded-md border px-3 py-2 text-[12px]",
          settled
            ? "border-policy-pass/40 bg-policy-pass/10"
            : "border-state-financed/30 bg-state-financed/10",
        )}
      >
        <div className="flex items-center justify-between">
          <span className="text-orbitra-paper">{settled ? "Tariff paid · single-window cleared" : "Tariff due before release"}</span>
          <span
            className={clsx(
              "font-display text-lg font-semibold",
              settled ? "text-policy-pass" : "text-state-financed",
            )}
          >
            {fmtUsd(t.totalUsd)}
          </span>
        </div>
        <p className={clsx(
          "mt-0.5 text-[10px] italic",
          settled ? "text-policy-pass/80" : "text-state-financed/80",
        )}>
          ~{((t.totalUsd / t.cifUsd) * 100).toFixed(1)}% of CIF · landed at customs
        </p>
      </div>

      <p className="mt-2 text-[10px] text-orbitra-muted">{t.methodologyLabel}</p>
    </div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-financed/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 font-display font-semibold text-orbitra-paper">{value}</p>
    </div>
  );
}
